import { Component, Element, h, Host, Listen, Prop, Watch } from '@stencil/core'

import { Breakpoint, BreakpointObserver, BREAKPOINTS } from '../../../utils/breakpoint-observer'
import Helpers from '../../../utils/helpers'
import { CssClassMap } from '../../../utils/interfaces'

/**
 * ## Design System
 *
 * Para a documentação completa de design, incluindo diretrizes de uso, acessibilidade e exemplos visuais, consulte o [Design System do GovBR](https://www.gov.br/ds/components/header?tab=designer).
 *
 * @slot default - Slot para a logo personalizada exibida no cabeçalho.
 */
@Component({
  tag: 'br-header-logo',
  styleUrl: 'header-logo.scss',
  shadow: true,
})
export class HeaderLogo {
  /**
   * Referência ao elemento host do componente.
   * Utilize esta propriedade para acessar e manipular o elemento do DOM associado ao componente.
   */
  @Element() el: HTMLBrHeaderLogoElement

  /**
   * Identificador único; gerado automaticamente quando omitido.
   */
  @Prop({ reflect: true }) readonly customId: string = Helpers.generateUniqueId()

  /**
   * Texto alternativo/descritivo da logo.
   */
  @Prop({ reflect: true }) description: string = 'logo'

  /**
   * URL de destino quando a logo for clicada.
   * Se fornecido, a logo será envolvida em um link.
   */
  @Prop({ reflect: true }) href?: string

  /**
   * Alterna para versão compacta quando true.
   */
  @Prop({ reflect: true, mutable: true }) isCompact: boolean = false

  /**
   * Target do link quando href é fornecido.
   */
  @Prop({ reflect: true }) target?: '_blank' | '_self' | '_parent' | '_top' = '_self'

  /**
   * Url da logo padrão.
   */
  @Prop({ reflect: true }) src: string = ''

  /**
   * Largura da imagem.
   */
  @Prop({ reflect: true }) width: string = null

  /**
   * Altura da imagem.
   */
  @Prop({ reflect: true, mutable: true }) height: string = null

  /**
   * Observador de mudanças de breakpoint.
   */
  private breakpointObserver: BreakpointObserver

  /**
   * Indica o breakpoint atual.
   */
  private currentBreakpoint: Breakpoint = BREAKPOINTS.LARGE

  /**
   * Indica se a altura padrão está sendo usada.
   */
  private useDefaultHeight: boolean = false

  /**
   * Listener para o evento `headerCompactChange` disparado pelo componente pai `br-header`.
   * O evento contém os detalhes do estado compacto e o ID do componente pai.
   * Se o ID do componente pai corresponder ao ID do `br-header` que contém este `br-header-logo`, o estado `isCompact` será atualizado.
   * @param event Evento disparado pelo componente pai `br-header` quando o estado compacto é alterado.
   */
  @Listen('headerCompactChange', { target: 'document', passive: true })
  private handleHeaderCompactChange(event: CustomEvent<{ parentId: string; isCompact: boolean }>) {
    if (event.detail.parentId === this.el.parentElement.shadowRoot.querySelector('.br-header').id) {
      this.isCompact = event.detail.isCompact
    }
  }

  /**
   * Observa mudanças na propriedade `isCompact` e ajusta a altura da logo se `hasDefaultHeight` for verdadeiro.
   */
  @Watch('isCompact')
  handleIsCompactChange() {
    this.setDefaultHeight()
  }

  /**
   * Ciclo de vida do componente.
   * Chamado quando o componente é conectado ao DOM.
   */
  connectedCallback() {
    this.setBreakpointObserver()
    this.setUseDefaultHeight()
  }

  /**
   * Ciclo de vida do componente. Chamado antes do componente ser carregado.
   */
  componentWillLoad() {
    // Garante que o listener para o evento `headerCompactChange` esteja ativo quando o componente for carregado.
    void this.handleHeaderCompactChange
    this.setDefaultHeight()
  }

  /**
   * Manipula as mudanças de breakpoint.
   * @param breakpoint O breakpoint ativo.
   */
  private handleBreakpointChange(breakpoint: Breakpoint) {
    this.currentBreakpoint = breakpoint
    this.setDefaultHeight()
  }

  /**
   * Retorna um objeto contendo as classes CSS a serem aplicadas ao componente.
   * @returns Um objeto contendo as classes CSS a serem aplicadas ao componente.
   */
  private getCssClassMap(): CssClassMap {
    return {
      'br-header-logo': true,
    }
  }

  /**
   * Configura o observador de mudanças de breakpoint.
   */
  private setBreakpointObserver() {
    this.breakpointObserver = new BreakpointObserver()
    this.breakpointObserver.subscribe(this.handleBreakpointChange.bind(this))
  }

  /**
   * Define a altura padrão da logo com base no estado compacto e no breakpoint atual.
   * O tamanho padrão depende do breakpoint atual e se está no modo compacto ou largo.
   * Modo largo:
   * - Breakpoint X_SMALL: 24px
   * - Breakpoint SMALL, MEDIUM, LARGE E X_LARGE 40px
   * Modo compacto:
   * - Breakpoint X_SMALL: 24px
   * - Breakpoint SMALL, MEDIUM, LARGE E X_LARGE 16px
   */
  private setDefaultHeight() {
    if (this.useDefaultHeight) {
      if (this.isCompact) {
        this.height = this.currentBreakpoint === BREAKPOINTS.X_SMALL ? '24' : '16'
      } else {
        this.height = this.currentBreakpoint === BREAKPOINTS.X_SMALL ? '24' : '40'
      }
    }
  }

  private setUseDefaultHeight() {
    this.useDefaultHeight = this.height === null || this.height === undefined
  }

  /**
   * Cria o elemento de imagem da logo.
   * @returns JSX do elemento img.
   */
  private renderImageElement() {
    return <img src={this.src} alt={this.description} width={this.width} height={this.height} />
  }

  /**
   * Renderiza o componente.
   * @returns JSX do componente.
   */
  render() {
    return (
      <Host>
        <slot>
          <div id={this.customId} class={this.getCssClassMap()}>
            {this.href && this.href.trim() !== '' ? (
              <a
                href={this.href}
                target={this.target}
                {...(this.target === '_blank' && { rel: 'noopener noreferrer' })}
              >
                {this.renderImageElement()}
              </a>
            ) : (
              this.renderImageElement()
            )}
          </div>
        </slot>
      </Host>
    )
  }
}
